import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ControllerExtensionsService } from "../Controller/ControllerExtensionsService";
import { IControllerExtensionsService } from "../Controller/IControllerExtensionsService";
import { DevicesService } from "../Devices/DevicesService";
import { IDevice } from "../Devices/IDevice";
import { IDevicesService } from "../Devices/IDevicesService";
import { ResponseResult } from "../FieldChangeResult";
import { ICustomUpdateActions } from "./CustomUpdateActions/ICustomUpdateActions";
import { CannotGetRuntimeErrorCode, CannotGetRuntimeInfo, SoftwareRuntimeInfo, UpdatesProvider } from "./UpdatesProvider";
import { IDeviceUpdateProviderService } from "./UpdateProviderServices/IDeviceUpdateProviderService";
import { ControllerUpdatesInfo, ControllerUpdateType, ControllerVersion, DeviceUpdateShortInfo, IControllerAvailableUpdateInfo, IDeviceUpdateInfo, UpdateHistory } from "./IUpdateInfo";
import { IUpdatesProvider, ProviderType, Update, UpdateInfo, UpdateTypes, UpdatesProviderNotFound } from "./IUpdatesProvider";
import { BulkUpdateResult, BulkUpdateStatus, DownloadProgress, DownloadProgressInfo, DownloadTask, IUpdatesService, Progress, UpdateAction, UpdateBranch, UpdateErrorCode, UpdateProgress, UpdateProgressInfo, UpdateState, UpdateStatus, UserInput } from './IUpdatesService';
import { IControllerModulesUpdatesProviderService } from "./UpdateProviderServices/IControllerModulesUpdatesProviderService";
import { AppState, IAppStateService } from "../IAppStateService";
import { AppStateService } from "../AppStateService";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { Helpers, ParseObjToMap, Task } from "../../Helpers";
import { ISessionService } from "../Session/ISessionService";
import { SessionService } from "../Session/SessionService";
import { IControllerConfigurationService } from "../Controller/IControllerConfigurationService";
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";

export class UpdatesService implements IUpdatesService {
    public static readonly ServiceName: string = "UpdatesService";

    private _updateProviders: IUpdatesProvider;
    private _controllerUpdates: CachedUpdateInfo[] = [];
    private _logger: ILoggerService;
    private _connection: IExalusConnectionService;
    private _appStateService: IAppStateService;
    private _extensionService: IControllerExtensionsService;
    private _updateRequestTimeout = 120000;
    private _oldUpdateVersion: number = 5;
    private _oldUpdateBuild: number = 56;
    private _updateOfflineStageTime = 15;

    constructor() {
        this._updateProviders = Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName);
        this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._appStateService = Api.Get<IAppStateService>(AppStateService.ServiceName);
        this._extensionService = Api.Get<IControllerExtensionsService>(ControllerExtensionsService.ServiceName);
    }
    async IsBranchAndAutomaticUpdatesSettingSupportedAsync(): Promise<boolean> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult)
            return false;

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return false;

        return true;
    }

    async DownloadControllerUpdateAsync<T extends ICustomUpdateActions>(downloadProgress: DownloadProgress): Promise<Status.OK | ResponseResult<UpdateErrorCode>> {
        // This method downloads the controller update using the new API, if available.
        // It does not install the update, only downloads it.
        try {
            const ver = await this.getRuntimeInfoAsync();
            if (ver instanceof ResponseResult) {
                return ver;
            }

            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

            // Only supported for new API (version > _oldUpdateVersion/_oldUpdateBuild)
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                return new ResponseResult<UpdateErrorCode>(
                    UpdateErrorCode.FeatureUnsupportedInCurrentVersion,
                    `Cannot download update, feature unsupported in current version. Current version: ${ver}`
                );
            }

            // Use the API to download the update (downloadOnly = true)
            return await this.DownloadControllerUpdateUsingApiAsync(
                () => { }, // No updateProgress callback needed for download-only
                downloadProgress,
                true
            );
        } catch (error) {
            return new ResponseResult<UpdateErrorCode>(
                UpdateErrorCode.CannotProcessUpdate,
                `Failed to download controller update: ${error}`
            );
        }
    }

    InstallControllerOrRadioUpdateAsync<T extends ICustomUpdateActions>(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, updateGuid: string): Promise<Status.OK | ResponseResult<UpdateErrorCode>>;
    InstallControllerOrRadioUpdateAsync<T extends ICustomUpdateActions>(updateProgress: UpdateProgress, downloadProgress: DownloadProgress): Promise<Status.OK | ResponseResult<UpdateErrorCode>>;
    async InstallControllerOrRadioUpdateAsync(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, updateGuid?: string): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {

        const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");
        const numberOfUpdates = this._controllerUpdates.length;

        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        //Check if any updates are avaliable
        if (this._controllerUpdates.length == 0)
            return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot find any updates, make sure you check for available updates by calling CheckControllerUpdateAsync().`);

        //Install single update
        if (updateGuid !== undefined && (typeof updateGuid === 'string') && regGuid.test(updateGuid as string)) {
            let updateResult: ResponseResult<UpdateErrorCode> | Status.OK;
            const updateToInstall = this._controllerUpdates.find(update => update.UpdateIdentifier == updateGuid);

            if (updateToInstall == null)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Update with requested guid (${updateGuid as string}) not found.`);

            this._logger.Debug(`Installing single update type: ${updateToInstall.Update.UpdateType}, resource: ${updateToInstall.UpdatedResourceName}, version: ${updateToInstall.Update.Version}`)

            this._appStateService.DisallowHibernation();

            switch (updateToInstall.UpdateType) {
                case ControllerUpdateType.RadioUpdate:
                    updateResult = await (await this._updateProviders.GetUpdatesProviderAsync<IControllerModulesUpdatesProviderService>(updateToInstall.ProviderName)).InstallRadioUpdateAsync(updateToInstall, updateProgress);
                    this._appStateService.AllowHibernation();
                    this._logger.Debug(`Radio update result: ${updateResult}`);
                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);

                    return updateResult;

                case ControllerUpdateType.SoftwareUpdate:
                    //Old way
                    if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                        updateResult = await this.UpdateControllerUsingScriptAsync(updateToInstall.Update, updateProgress);
                        this._appStateService.AllowHibernation();

                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);

                    }
                    //New way
                    else {
                        let availableUpdate = await this.CheckControllerUpdatesAsync();
                        //let isDownloaded = availableUpdate.UpdatesAvailable.any(a => a.IsDownloadOnly);

                        //if (!isDownloaded) {
                        //    await this.DownloadControllerUpdateAsync(downloadProgress);
                        //}
                        updateResult = await this.UpdateControllerUsingApiAsync(updateProgress, downloadProgress, false);
                        this._appStateService.AllowHibernation();

                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);
                    }

                    return updateResult;

                default:
                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update with type ${updateToInstall.UpdateType} is not supported by UpdateService.`)
            }
        }
        //Install all updates
        else {
            //Handle update progress with multiple updates.
            const updateNumberRef = [1];

            const updateProgressWrapper = (progressInfo: UpdateProgressInfo) => {
                progressInfo.UpdateNumber = updateNumberRef[0];
                progressInfo.AvailableUpdates = numberOfUpdates;

                updateProgress(progressInfo);
            }

            const timeout = async (ms: number) => {
                return new Promise(resolve => setTimeout(resolve, ms));
            };

            //First insall radio updates
            let updatesToInstall = this._controllerUpdates.where(update => update.UpdateType == ControllerUpdateType.RadioUpdate).toArray();
            let result = [];

            this._appStateService.DisallowHibernation();
            for (let i = 0; i < updatesToInstall.length; i++) {
                const updateResult = await (await this._updateProviders.GetUpdatesProviderAsync<IControllerModulesUpdatesProviderService>(updatesToInstall[i].ProviderName)).InstallRadioUpdateAsync(updatesToInstall[i], updateProgressWrapper);

                //Wait between updates
                await timeout(5000);

                if (updateResult == Status.OK)
                    this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updatesToInstall[i].UpdateIdentifier);

                result.push(updateResult)
                updateNumberRef[0]++;
            }

            //Install other updates (in the future)
            //....//
            //

            //Install controller update
            const controlerUpdate = this._controllerUpdates.find(update => update.UpdateType == ControllerUpdateType.SoftwareUpdate);

            if (controlerUpdate != null) {
                //Old way
                if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                    const updateResult = await this.UpdateControllerUsingScriptAsync(controlerUpdate.Update, updateProgressWrapper);

                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != controlerUpdate.UpdateIdentifier);

                    result.push(updateResult)
                    updateNumberRef[0]++;
                }
                //New way
                else {
                    const updateResult = await this.UpdateControllerUsingApiAsync(updateProgressWrapper, downloadProgress, false);
                    this._appStateService.AllowHibernation();

                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);

                    result.push(updateResult)
                    updateNumberRef[0]++;
                }
            }

            const resultWithError = result.where(r => r instanceof ResponseResult).toArray();
            if (resultWithError != null && resultWithError.length != 0) {
                let errors: string = "";
                resultWithError.forEach(err => errors += `${(err as ResponseResult<UpdateErrorCode>).Type}, `);
                this._appStateService.AllowHibernation();

                if (resultWithError.any(err => (err as ResponseResult<UpdateErrorCode>).Type == UpdateErrorCode.UpdateDownloadFailed))
                    return new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed - not all updates were successfully downloaded. Installation error codes: ${errors}`);

                return new ResponseResult(UpdateErrorCode.UpdatesPartiallyInstalled, `Update installation finished - not all updates were successfully installed. Installation error codes: ${errors}`);
            }

            this._appStateService.AllowHibernation();
            return Status.OK;
        }
    }

    async GetAutomaticControllerUpdateDownloadStatusAsync(): Promise<boolean | ResponseResult<UpdateErrorCode>> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot get update branch, feature unsupported in current version. Current version: ${ver}`);

        let result = await this._connection.SendAndWaitForResponseAsync<DataFrame<boolean>>(new GetAutomaticUpdatesStatusRequest(), 10000, false);

        if (result.Status == Status.OK && result.Data)
            return true;
        else
            return false;
    }

    GetServiceName(): string {
        return UpdatesService.ServiceName;
    }

    CheckDeviceVersionAsync(deviceGuid: string): Promise<string | ResponseResult<UpdateErrorCode>>;
    CheckDeviceVersionAsync(device: IDevice): Promise<string | ResponseResult<UpdateErrorCode>>;
    async CheckDeviceVersionAsync(dev: unknown): Promise<string | ResponseResult<UpdateErrorCode>> {
        let device: IDevice;

        if (typeof dev == 'string') {
            const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

            if (result != null)
                device = result;
            else
                return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
        }
        else {
            device = dev as IDevice;
        }

        if (device.ProtocolGuid == null)
            return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);

        const softwareInfo = await (await this._updateProviders.GetUpdatesProvidersByProtocolAsync<IDeviceUpdateProviderService>(device.ProtocolGuid, ProviderType.Device)).first()?.GetSoftwareVersionAsync(device);
        if ((softwareInfo as ResponseResult<any>).Type != null)
            return softwareInfo as ResponseResult<any>;

        return softwareInfo as string;
    }

    CheckDeviceUpdateAsync(deviceGuid: string): Promise<IDeviceUpdateInfo | ResponseResult<UpdateErrorCode>>;
    CheckDeviceUpdateAsync(device: IDevice): Promise<IDeviceUpdateInfo | ResponseResult<UpdateErrorCode>>;
    async CheckDeviceUpdateAsync(dev: unknown): Promise<IDeviceUpdateInfo | ResponseResult<UpdateErrorCode>> {
        let device: IDevice;

        if (typeof dev == 'string') {
            const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

            if (result != null)
                device = result;
            else
                return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
        }
        else {
            device = dev as IDevice;
        }

        if (device.ProtocolGuid == null)
            return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);

        const updateInfo = await (await this._updateProviders.GetUpdatesProvidersByProtocolAsync<IDeviceUpdateProviderService>(device.ProtocolGuid, ProviderType.Device)).first()?.GetAvailableUpdateAsync(device);
        if ((updateInfo as ResponseResult<UpdateErrorCode>).Type != null)
            return updateInfo as ResponseResult<UpdateErrorCode>;

        const result = new DeviceUpdate();
        result.CurrentResourceVersion = (updateInfo as UpdateInfo).CurrentResourceVersion;
        result.NewResourceVersion = (updateInfo as UpdateInfo).Update.Version;
        result.UpdatedResourceName = (updateInfo as UpdateInfo).UpdatedResourceName;
        result.UpdateResourceGuid = (updateInfo as UpdateInfo).Update.ResourceGuid;
        result.UpdateDescription = new Map<string, string>([["pl-PL", (updateInfo as UpdateInfo).Update.Description]]);

        let protocol = "n/a"
        try {
            protocol = (await this._extensionService.GetProtocolInfoAsync(device.ProtocolGuid)).first().Name;
        } catch (error) {
            this._logger.Warning(`Cannot get protocol name! error: ${error}`);
        }

        result.Protocol = protocol
        result.ProtocolGuid = device.ProtocolGuid;

        if ((await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync()).any(dev => dev.ModelGuid == "952ddc14-76c4-4f47-b0bc-5d106c1f9b02"))
            result.IsUpdateSafe = false;

        return result;
    }

    async CheckDeviceUpdatesBulkAsync(reloadCache?: boolean): Promise<IDeviceUpdateInfo[] | ResponseResult<UpdateErrorCode>> {

        const updateProviders = await (await this._updateProviders.GetUpdatesProvidersAsync<IDeviceUpdateProviderService>(ProviderType.Device))
        const result: DeviceUpdate[] = []

        for (var provider of updateProviders) {
            const updateInfo = await provider.CheckUpdateBulkAsync(reloadCache);

            if (updateInfo instanceof ResponseResult)
                return updateInfo as ResponseResult<UpdateErrorCode>;

            for (const d of updateInfo) {
                const update = new DeviceUpdate();
                update.CurrentResourceVersion = d.CurrentResourceVersion;
                update.NewResourceVersion = d.Update.Version;
                update.UpdatedResourceName = d.UpdatedResourceName;
                update.UpdateDescription = d.UpdateDescription;
                update.IsUpdateSafe = true;

                let protocol = "n/a"
                try {
                    protocol = (await this._extensionService.GetProtocolInfoAsync(provider.ProtocolGuid)).first().Name;
                } catch (error) {
                    this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                }
                update.Protocol = protocol;

                update.ProtocolGuid = provider.ProtocolGuid;
                update.UpdateResourceGuid = d.Update.ResourceGuid;

                result.push(update);
            }
        }

        return result;
    }

    InstallDeviceUpdateAsync<T extends ICustomUpdateActions>(device: IDevice, updateProgress: UpdateProgress, updateAction?: UpdateAction<T> | undefined, force?: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK>;
    InstallDeviceUpdateAsync<T extends ICustomUpdateActions>(dev: string, updateProgress: UpdateProgress, updateAction?: UpdateAction<T> | undefined, force?: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK>;
    async InstallDeviceUpdateAsync<T extends ICustomUpdateActions>(dev: unknown, updateProgress: UpdateProgress, updateAction?: UpdateAction<T>, force = false): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        let device: IDevice;

        if (typeof dev == 'string') {
            const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

            if (result != null)
                device = result;
            else
                return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
        }
        else {
            device = dev as IDevice;
        }

        if (device.ProtocolGuid == null)
            return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);

        const updateInfo = await (await this._updateProviders.GetUpdatesProvidersByProtocolAsync<IDeviceUpdateProviderService>(device.ProtocolGuid, ProviderType.Device)).first()?.GetAvailableUpdateAsync(device);
        if ((updateInfo as ResponseResult<UpdateErrorCode>).Type != null)
            return updateInfo as ResponseResult<UpdateErrorCode>;

        this._appStateService.DisallowHibernation();

        const updateInstallResult = await (await this._updateProviders.GetUpdatesProvidersByProtocolAsync<IDeviceUpdateProviderService>(device.ProtocolGuid, ProviderType.Device)).first().InstallUpdateAsync((updateInfo as UpdateInfo), updateProgress, updateAction, force);
        if ((updateInstallResult as ResponseResult<UpdateErrorCode>).Type != null) {
            this._appStateService.AllowHibernation();
            return updateInstallResult as ResponseResult<UpdateErrorCode>;
        }

        this._appStateService.AllowHibernation();
        return Status.OK;
    }

    async InstallDevicesUpdateBulkAsync<T extends ICustomUpdateActions>(deviceGuids: string[], updateProgress: UpdateProgress, updateAction?: UpdateAction<T>): Promise<BulkUpdateResult | ResponseResult<UpdateErrorCode>> {
        const grouppedDevs = (await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync()).where(d => deviceGuids.includes(d.Guid)).groupBy(d => d.ProtocolGuid!);
        const result: BulkUpdateResult[] = [];
        this._appStateService.DisallowHibernation();

        const grpCnt = grouppedDevs.count();
        for (let i = 0; i < grpCnt; i++) {
            const grp = grouppedDevs.elementAt(i);

            const totalStartRange = i * (100 / grpCnt);
            const totalEndRange = (i + 1) * (100 / grpCnt);

            const progressFunctionWrapper = (progress: UpdateProgressInfo) => {
                progress.PercentageTotal = Math.floor((progress.PercentageTotal / 100) * (totalEndRange - totalStartRange) + totalStartRange) as Progress;
                updateProgress(progress);
            }

            const updateProvider = await (await this._updateProviders.GetUpdatesProvidersByProtocolAsync<IDeviceUpdateProviderService>(grp.key, ProviderType.Device))
            const r = await updateProvider.first().InstalUpdateBulkAsync(grp.select(d => d.Guid).toArray(), progressFunctionWrapper, updateAction);
            if (r instanceof ResponseResult)
                return r as ResponseResult<UpdateErrorCode>;

            result.push(r as BulkUpdateResult);
        }
        this._appStateService.AllowHibernation();

        if (result.length == 1)
            return result[0];
        else {
            const newBulkResult = new BulkUpdateResult();

            if (result.some(r => r.DevicesUpdateResults.count() > 0) && result.any(r => r.BulkUpdateStatus != BulkUpdateStatus.Success)) {
                newBulkResult.BulkUpdateStatus = BulkUpdateStatus.PartialSuccess;
            }
            else if (result.any(r => r.BulkUpdateStatus != BulkUpdateStatus.Success)) {
                newBulkResult.BulkUpdateStatus = result.first().BulkUpdateStatus;
            } else {
                newBulkResult.BulkUpdateStatus = BulkUpdateStatus.Success;
            }

            for (const r of result) {
                r.DevicesUpdateResults.forEach((value, key) => {
                    newBulkResult.DevicesUpdateResults.set(key, value);
                });
            }
            return newBulkResult;
        }
    }

    async CheckControllerSoftwareUpdateAvailabilityAsync(): Promise<boolean> {
        const user = Api.Get<ISessionService>(SessionService.ServiceName).User;

        if (user == null)
            return false;

        //Very old software, does not contains information about software version (before 15.02.2024)
        if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
            return true;

        //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
        const [major, build] = user.SoftwareVersion.split('.');
        if (parseFloat(major) < this._oldUpdateVersion || (parseFloat(major) == this._oldUpdateVersion && parseFloat(build) <= this._oldUpdateBuild))
            return true;

        const result = await this._connection.SendAndWaitForResponseAsync<PendingUpdateApiObject>(new PendingUpdatesRequest(), 10000, false);

        if (result == null || result.Status != Status.OK || result.Data == null)
            return false;

        const [majorPening, buildPending] = result.Data.Update.Version.ContainerSoftwareVersion.toString().split('.');

        if (parseInt(majorPening) > parseInt(major) || (parseInt(majorPening) == parseInt(major) && parseInt(buildPending) > parseInt(build)))
            return true;
        else
            return false;
    }

    CanCancelFailedControllerUpdateOrGetPendingUpdateProgress(): boolean {
        return Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).CheckIfControllerSoftwareVersionIsEnough(6, 66);
    }

    async CancelFailedControllerUpdateAsync(): Promise<Status.OK | Status.Error | Status.OperationNotPermitted> {

        const result = await this._connection.SendAndWaitForResponseAsync<any>(new CancelFailedUpdateRequest(), 10000, false);

        switch (result.Status) {
            case Status.OK:
                return Status.OK;

            case Status.OperationNotPermitted:
                return Status.OperationNotPermitted;

            case Status.Error:
            default:
                return Status.Error;
        }
    }

    async CheckControllerPendingUpdateProgressAsync(updateProgress: UpdateProgress, downloadProgess: DownloadProgress): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        return new Promise<ResponseResult<UpdateErrorCode> | Status.OK>(async (resolve, reject) => {
            const user = Api.Get<ISessionService>(SessionService.ServiceName).User;

            if (user == null)
                return resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateActionsNotSupported, "No user data found, cannot check pending update progress."));

            //Very old software, does not contains information about software version (before 15.02.2024)
            if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
                return resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetCurrentControllerVersion, "No software version data found, cannot check pending update progress."));

            //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
            const [major, build] = user.SoftwareVersion.split('.');
            if (parseFloat(major) < this._oldUpdateVersion || (parseFloat(major) == this._oldUpdateVersion && parseFloat(build) <= this._oldUpdateBuild))
                return resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UnsupportedApiVersion, "Not supported API version, cannot check pending update progress."));

            const pendingUpdateResult = await this._connection.SendAndWaitForResponseAsync<PendingUpdateApiObject>(new PendingUpdatesRequest(), 10000, false);
            let isAnyUpdatePending = pendingUpdateResult.Status == Status.OK;

            if (!isAnyUpdatePending)
                return resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdates, "No pending updates found."));

            let downloadOnly = pendingUpdateResult.Data?.DownloadOnly;
            let endPercentage = 100;
            const totalTime = this._updateOfflineStageTime * 60 * 1000; //  minutes
            let percentage = 0;
            const info = new UpdateProgressInfo();
            let downloadProgressEndPercentage = 50;
            let dp = new DownloadProgressInfo();
            let up = new UpdateProgressInfo();
            let receivedFirstFrame = false;

            if (!downloadOnly) {
                dp.DownloadOnly = downloadOnly as boolean;
                downloadProgressEndPercentage = 100;
            }

            let downloadProgressOnePercentageMultiplier = 100 / downloadProgressEndPercentage;

            const handleReponse = (data: DataFrame<UpdateProgressResponse>) => {
                let tmp = data.Data as UpdateProgressResponse;
                this._logger.Debug(`Update progress: ${tmp.UpdateStep}, ${tmp.StepProgressInPercentage}%, ${tmp.UpdateProgressInPercentage}% data frame status: ${data.Status}`);

                info.Percentage = Math.round(tmp.StepProgressInPercentage) as Progress;
                info.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage) as Progress;
                dp.SpeedMbps = tmp.DownloadSpeedInMbs;
                dp.Percentage = Math.round(tmp.StepProgressInPercentage) as Progress;
                dp.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage) as Progress;
                dp.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);

                up.AvailableUpdates = 1;
                up.Percentage = tmp.StepProgressInPercentage;
                up.PercentageTotal = tmp.UpdateProgressInPercentage;
                up.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                percentage = tmp.StepProgressInPercentage;

                switch (tmp.UpdateStep) {
                    case UpdateStep.DownloadingInstaller:
                        dp.CurrentTask = DownloadTask.DownloadingInstaller;
                        up.Status = UpdateStatus.DownloadingPackage;
                        downloadProgess(dp);
                        //updateProgress(up);
                        break;

                    case UpdateStep.DownloadingUpdate:
                        dp.CurrentTask = DownloadTask.DownloadingUpdate;
                        up.Status = UpdateStatus.DownloadingPackage;
                        downloadProgess(dp);
                        //updateProgress(up);
                        break;

                    case UpdateStep.CheckingUpdate:
                        dp.CurrentTask = DownloadTask.CheckingUpdate;
                        up.Status = UpdateStatus.CheckingPackage;
                        //downloadProgess(dp);
                        updateProgress(up);
                        break;

                    case UpdateStep.PreparingUpdate:
                        up.Status = UpdateStatus.PreparingForUpgrade;
                        dp.CurrentTask = DownloadTask.CheckingUpdate;
                        updateProgress(up);
                        break;

                    case UpdateStep.OfflineUpdateStep:
                    case UpdateStep.InstallingUpdate:
                        up.Status = UpdateStatus.Upgrading;
                        updateProgress(up);
                        break;

                    case UpdateStep.InstallerFileSignCheckFailed:
                        up.Status = UpdateStatus.InstallationError;
                        updateProgress(up);
                        break;

                    case UpdateStep.UpdateFileSignCheckFailed:
                        up.Status = UpdateStatus.InstallationError;
                        updateProgress(up);
                        break;

                    case UpdateStep.UpdateDownloadFailed:
                        up.Status = UpdateStatus.InstallationStopped;
                        updateProgress(up);
                        break;

                    case UpdateStep.UpdateInstallationFailed:
                        up.Status = UpdateStatus.InstallationError;
                        updateProgress(up);
                        break;

                    case UpdateStep.UpdateFileCheckFailed:
                        up.Status = UpdateStatus.IncompatiblePackage;
                        updateProgress(up);
                        break;

                    case UpdateStep.UpdateDownloaded:
                        dp.Percentage = 100;

                        if (downloadOnly)
                            dp.PercentageTotal = Math.round((tmp.UpdateProgressInPercentage as number) * downloadProgressOnePercentageMultiplier) as Progress;
                        else
                            dp.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage) as Progress;

                        dp.SpeedMbps = 0;
                        dp.CurrentTask = DownloadTask.CheckingUpdate;
                        downloadProgess(dp);
                        dp.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                        downloadProgess(dp);
                        break;
                }
            };

            let intervalId: any = null;
            const onReconnect = async (state: AppState) => {
                if (state == AppState.Connected) {
                    clearInterval(intervalId);
                    info.Status = UpdateStatus.Upgraded;
                    info.Percentage = 100;
                    info.PercentageTotal = 100;
                    updateProgress(info);
                    clearInterval(intervalId);
                    await Task.Delay(5000);
                    resolve(Status.OK);
                    Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Unsubscribe(onReconnect);
                }
            };
            const result = await this._connection.SendAndHandleResponseAsync<UpdateProgressResponse>(new PendingUpdateProgressRequest(), 100_000, (data) => {
                switch (data.Status) {
                    case Status.MultiDataResponseStart:
                        if (!receivedFirstFrame) {
                            if (data.Data?.UpdateStep == UpdateStep.UpdateDownloaded)
                                endPercentage = 100;
                            else
                                endPercentage = 50; // 50% for download progress

                            receivedFirstFrame = true;
                        }
                        handleReponse(data);
                        break;

                    case Status.MultiDataResponse:
                        handleReponse(data);
                        break;

                    case Status.MultiDataResponseStop:
                        switch (data.Data?.UpdateStep) {
                            case UpdateStep.UpdateInstallationFailed:
                                up.Status = UpdateStatus.InstallationError;
                                updateProgress(up);
                                info.Percentage = 0;
                                info.PercentageTotal = 0;
                                updateProgress(info);
                                resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                                break;

                            case UpdateStep.InstallerFileSignCheckFailed:
                            case UpdateStep.UpdateFileSignCheckFailed:
                            case UpdateStep.UpdateDownloadFailed:
                            case UpdateStep.UpdateFileCheckFailed:
                                up.Status = UpdateStatus.DownloadingFailure;
                                updateProgress(up);
                                info.Percentage = 0;
                                info.PercentageTotal = 0;
                                updateProgress(info);
                                resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateDownloadFailed, `Update download failed!`));
                                break;

                            case UpdateStep.OfflineUpdateStep:
                                handleReponse(data);
                                info.Percentage = Math.round(data.Data.StepProgressInPercentage) as Progress;
                                info.PercentageTotal = Math.round(data.Data.UpdateProgressInPercentage) as Progress;
                                let oneUpdateProgressPercent = (100 - info.PercentageTotal) / 100;
                                let updateProgressPercent = info.PercentageTotal;
                                //info.PercentageTotal = resp.Data.UpdateProgressInPercentage as Progress; // 78%

                                const incrementTime = totalTime / (endPercentage - percentage);
                                intervalId = setInterval(() => {
                                    percentage++;
                                    updateProgressPercent += oneUpdateProgressPercent;

                                    if (updateProgressPercent > 100)
                                        updateProgressPercent = 100;

                                    if (percentage >= endPercentage) {
                                        info.Status = UpdateStatus.Upgraded;
                                        info.Percentage = 100;
                                        info.PercentageTotal = 100;
                                        updateProgress(info);
                                        clearInterval(intervalId);
                                        resolve(Status.OK);
                                    }
                                    else {
                                        info.Status = UpdateStatus.Upgrading;
                                        info.Percentage = Math.round(percentage) as Progress;
                                        info.PercentageTotal = Math.round(updateProgressPercent) as Progress;
                                        updateProgress(info);
                                    }
                                }, incrementTime);

                                Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Subscribe(onReconnect);
                                break;


                            case UpdateStep.UpdateDownloaded:
                                dp.Percentage = 100;

                                if (downloadOnly)
                                    dp.PercentageTotal = Math.round((data.Data?.UpdateProgressInPercentage as number) * downloadProgressOnePercentageMultiplier) as Progress;
                                else
                                    dp.PercentageTotal = Math.round(data.Data?.UpdateProgressInPercentage) as Progress;

                                dp.SpeedMbps = 0;
                                dp.CurrentTask = DownloadTask.CheckingUpdate;
                                downloadProgess(dp);
                                dp.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                downloadProgess(dp);

                                if (downloadOnly)
                                    resolve(Status.OK);
                                break;

                            default:
                                handleReponse(data);
                                resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotProcessUpdate, `Update installation failed, received UpdateStep code: ${data.Data?.UpdateStep}!`));
                                break;
                        }
                        break;

                    default:
                        reject(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdates, `Cannot get pending update progress, response is empty or status is not OK. Status: ${data?.Status}`));
                }
            }, true);
        });
    }

    async CheckDevicesUpdatesAvailabilityAsync(): Promise<DeviceUpdateShortInfo[]> {
        const user = Api.Get<ISessionService>(SessionService.ServiceName).User;

        if (user == null)
            return [];

        //Very old software, does not contains information about software version (before 15.02.2024)
        if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
            return [];

        //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
        const [major, build] = user.SoftwareVersion.split('.');
        if (parseFloat(major) < 6 || (parseFloat(major) == 6 && parseFloat(build) <= 4))
            return [];

        const result = await this._connection.SendAndWaitForResponseAsync<DeviceUpdateInfoApiObject[]>(new CheckDevicesUpdatesRequest(), 10000, false);
        const fResult: DeviceUpdateShortInfo[] = [];

        if (result == null || result?.Status != Status.OK || result?.Data == null)
            return [];

        result?.Data.forEach(d => {
            if (!d.IsAvailable)
                return;

            const info = new DeviceUpdateShortInfo();
            info.DeviceGuid = d.DeviceGuid;
            info.CurrentVersion = d.CurrentVersion;
            info.NewVersion = d.NewVersion;

            fResult.push(info);
        });

        return fResult;
    }

    async CheckControllerUpdatesAsync(): Promise<ControllerUpdatesInfo> {
        this._controllerUpdates = [];
        let updateProviderServices: IControllerModulesUpdatesProviderService[] = [];
        const updates: ControllerUpdatesInfo = new ControllerUpdatesInfo();

        const runtimeInfo = await this.getRuntimeInfoAsync();

        if (runtimeInfo instanceof ResponseResult) {
            const error = new ControllerUpdateNotAvailableInfo();
            error.UpdateType = ControllerUpdateType.SoftwareUpdate;
            error.ResponseResult = runtimeInfo;
            updates.UpdatesNotAvailable.push(error);
            return updates;
        }

        const swVerInt = parseInt(runtimeInfo.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(runtimeInfo.SoftwareVersion.toString().split('.')[1]);

        try {
            updateProviderServices = await this._updateProviders.GetUpdatesProvidersAsync<IControllerModulesUpdatesProviderService>(ProviderType.Controller);
        } catch (error) {
            if (error instanceof UpdatesProviderNotFound) {
                this._logger.Warning(`Cannot get update providers for controller protocols! Avaliable only controller software updates!`);
            }
            else throw error;
        }

        //Radio updates
        for (let i = 0; i < updateProviderServices.length; i++) {
            const result = await updateProviderServices[i].GetAvailableRadioUpdateAsync();

            if ((result as ResponseResult<UpdateErrorCode>).Type != null) {
                const error = new ControllerUpdateNotAvailableInfo();
                error.ResponseResult = result as ResponseResult<UpdateErrorCode>;
                error.UpdateType = ControllerUpdateType.RadioUpdate;

                let protocol = "n/a"
                try {
                    protocol = (await this._extensionService.GetProtocolInfoAsync(updateProviderServices[i].ProtocolGuid)).first().Name;
                } catch (error) {
                    this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                }

                error.UpdatedResourceName = new Map<string, string>([["pl-PL", protocol], ["en-US", protocol]]);
                error.CurrentResourceVersion = await updateProviderServices[i].GetCurrentRadioVersionAsync();

                updates.UpdatesNotAvailable.push(error);
            }
            else {
                const updateInfo = result as UpdateInfo;

                const update = new ControllerUpdate();
                update.UpdatedResourceName = new Map<string, string>([["pl-PL", updateInfo.UpdatedResourceName], ["en-US", updateInfo.UpdatedResourceName]]);
                update.CurrentResourceVersion = updateInfo.CurrentResourceVersion;
                update.NewResourceVersion = updateInfo.Update.Version;
                update.UpdateDescription = new Map<string, string>([["pl-PL", updateInfo.Update.Description]]);
                update.UpdateType = ControllerUpdateType.RadioUpdate;
                update.UpdateIdentifier = Helpers.GenerateUUID();
                update.UpdateIsDownloading = false;
                update.DownloadProgress = 100;
                update.UpdateCreationTime = null;

                updates.UpdatesAvailable.push(update);
                this._controllerUpdates.push({ ...updateInfo, UpdateIdentifier: update.UpdateIdentifier, ProviderName: updateProviderServices[i].GetUpdateProviderName(), UpdateType: ControllerUpdateType.RadioUpdate });
            }
        }

        let isNewWayUpdate = true;

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            isNewWayUpdate = false;

        //Software updates (new way)
        if (isNewWayUpdate) {
            const result = await this._connection.SendAndWaitForResponseAsync<PendingUpdateApiObject>(new PendingUpdatesRequest(), 15000, false);

            if (result == null || (result.Status != Status.OK && result.Status != Status.NoData))
                throw (new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result?.Status}`));

            if (result.Data != null) {
                const updateInfo = new UpdateInfo();
                updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                updateInfo.UpdateDescription = ParseObjToMap<string, string>(result.Data.Update.UpdateDescription);
                updateInfo.UpdatedResourceName = ParseObjToMap<string, string>(result.Data.Update.UpdateName).get("en-US") as string;

                const update = new ControllerUpdate();
                update.UpdatedResourceName = ParseObjToMap<string, string>(result.Data.Update.UpdateName);
                update.UpdateDescription = ParseObjToMap<string, string>(result.Data.Update.UpdateDescription);
                update.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                update.NewResourceVersion = `${result.Data.Update.Version.BaseRuntime}.${result.Data.Update.Version.ContainerSoftwareVersion}`;
                update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                update.UpdateIdentifier = result.Data.Update.Guid;
                update.UpdateIsDownloading = (result.Data.UpdateStep == UpdateStep.DownloadingInstaller
                    || result.Data.UpdateStep == UpdateStep.DownloadingUpdate
                    || result.Data.UpdateStep == UpdateStep.CheckingUpdate) ? true : false;
                update.DownloadProgress = result.Data.DownloadProgress;
                update.UpdateCreationTime = result.Data.Update.Creationtime;
                update.IsDownloadOnly = result.Data.DownloadOnly;

                updates.UpdatesAvailable.push(update);
                this._controllerUpdates.push({ ...updateInfo, UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate });

            }
            else {
                //If no updates - returns status NoData
                const result = await this._connection.SendAndWaitForResponseAsync<UpdateApiObject>(new CheckUpdatesRequest(), 15000, false);

                if (result == null)
                    throw new Error(`Cannot check updates! Response is empty.`);

                switch (result.Status) {
                    case Status.NoData:
                        const error = new ControllerUpdateNotAvailableInfo();
                        error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                        error.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        error.ResponseResult = new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `Updates not found, controller is already up-to-date.`);
                        updates.UpdatesNotAvailable.push(error);
                        break;

                    case Status.OK:
                        if (result.Data == null)
                            throw new Error(`Cannot check updates! Response data is empty.`);

                        const updateInfo = new UpdateInfo();
                        updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                        updateInfo.UpdateDescription = ParseObjToMap<string, string>(result.Data.UpdateDescription);
                        updateInfo.UpdatedResourceName = ParseObjToMap<string, string>(result.Data.UpdateName).get("en-US") as string;

                        const update = new ControllerUpdate();
                        update.UpdatedResourceName = ParseObjToMap<string, string>(result.Data.UpdateName);
                        update.UpdateDescription = ParseObjToMap<string, string>(result.Data.UpdateDescription);
                        update.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                        update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                        update.UpdateIdentifier = result.Data.Guid;
                        update.NewResourceVersion = `${result.Data.Version.BaseRuntime}.${result.Data.Version.ContainerSoftwareVersion}`;
                        update.UpdateIsDownloading = false;
                        update.DownloadProgress = 0;
                        update.UpdateCreationTime = result.Data.Creationtime;

                        updates.UpdatesAvailable.push(update);
                        this._controllerUpdates.push({ ...updateInfo, UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate });
                        break;

                    default:
                        throw new Error(`Cannot check updates! Response status is not OK. Status: ${result.Status}`);
                }
            }
        }
        //Software updates (old way)
        else {
            const url = `https://exalus-updates.tr7.pl/software/${runtimeInfo.UpdateChannel}/${runtimeInfo.RuntimeVersion}/${runtimeInfo.SoftwareVersion}/update.json`;

            try {
                const response = await fetch(url);
                let updateData;
                let updateInfo = new UpdateInfo();

                const error = new ControllerUpdateNotAvailableInfo();
                error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                error.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);

                switch (response.status) {
                    case 200:
                        if (response.body != null)
                            updateData = await response.json() as Update;
                        break;
                    case 404:
                        error.ResponseResult = new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `Updates not found, controller is already up-to-date.`);
                        updates.UpdatesNotAvailable.push(error);
                        break;
                    default:
                        error.ResponseResult = new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Response status code does not indicate success. Status: ${response.status}`);
                        updates.UpdatesNotAvailable.push(error);
                        break;
                }

                if (updateData != null) {
                    updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                    updateInfo.Update = updateData;

                    const update = new ControllerUpdate();
                    update.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    update.CurrentResourceVersion = updateInfo.CurrentResourceVersion;
                    update.NewResourceVersion = updateInfo.Update.Version;
                    update.UpdateDescription = new Map<string, string>([["pl-PL", updateInfo.Update.Description]]);
                    update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                    update.UpdateIdentifier = Helpers.GenerateUUID();
                    update.DownloadProgress = 100;
                    update.UpdateIsDownloading = false;
                    update.UpdateCreationTime = null;

                    updates.UpdatesAvailable.push(update);
                    this._controllerUpdates.push({ ...updateInfo, UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate });
                }
                else if (response.status == 200)
                    throw (new Error(`Cannot get Update Data from response. Status: ${response.status}`));

            } catch (err) {
                const runtimeInfo = (await this.getRuntimeInfoAsync());

                if (runtimeInfo instanceof ResponseResult) {
                    const error = new ControllerUpdateNotAvailableInfo();
                    error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                    error.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    error.ResponseResult = runtimeInfo;
                    updates.UpdatesNotAvailable.push(error);
                }
                else {
                    const error = new ControllerUpdateNotAvailableInfo();
                    error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                    error.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    error.ResponseResult = new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get available updates! ${err}`);
                    error.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;

                    updates.UpdatesNotAvailable.push(error);
                }
            }
        }

        return updates;
    }

    async CheckControllerVersionAsync(): Promise<ControllerVersion[] | ResponseResult<UpdateErrorCode>> {

        const updateProviderServices: IControllerModulesUpdatesProviderService[] = await this._updateProviders.GetUpdatesProvidersAsync<IControllerModulesUpdatesProviderService>(ProviderType.Controller);
        const runtimeInfo = (await this.getRuntimeInfoAsync());

        if (runtimeInfo instanceof ResponseResult) {
            return runtimeInfo;
        }

        const result: ControllerVersion[] = []
        let version;

        //Software updates
        version = new ControllerVersion();
        version.Resource = "Controller software";
        version.Type = ControllerUpdateType.SoftwareUpdate;
        version.Version = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
        result.push(version);

        //Radio updates
        for (let i = 0; i < updateProviderServices.length; i++) {
            version = new ControllerVersion();
            version.Version = await updateProviderServices[i].GetCurrentRadioVersionAsync();
            version.Type = ControllerUpdateType.RadioUpdate;

            let protocol = "n/a"
            try {
                protocol = (await this._extensionService.GetProtocolInfoAsync(updateProviderServices[i].ProtocolGuid)).first().Name;
            } catch (error) {
                this._logger.Warning(`Cannot get protocol name! error: ${error}`);
            }

            version.Resource = protocol;

            result.push(version);
        }

        return result;
    }

    async GetControllerUpdateBranchAsync(): Promise<ResponseResult<UpdateErrorCode> | UpdateBranch> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot get update branch, feature unsupported in current version. Current version: ${ver}`);

        const result = await this._connection.SendAndWaitForResponseAsync<number>(new GetUpdateBranchRequest(), 10000, false);

        if (result == null || result?.Status != Status.OK)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdatesBranch, `Cannot get update branch, response is empty or status is not OK. Status: ${result?.Status}`);

        switch (result.Data) {
            case 0:
                return UpdateBranch.Public;
            case 1:
                return UpdateBranch.Beta;
            case 2:
                return UpdateBranch.Development;
            default:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdatesBranch, `Cannot get update branch, unknown branch number. Branch number: ${result.Data}`);
        }
    }

    async SetControllerUpdateBranchAsync(updateBranch: UpdateBranch): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot set update branch, feature unsupported in current version. Current version: ${ver}`);

        const result = await this._connection.SendAndWaitForResponseAsync<any>(new SetUpdateBranchRequest(updateBranch), 10000, false);

        if (result == null || result?.Status != Status.OK)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot set update branch, response is empty or status is not OK. Status: ${result?.Status}`);

        return Status.OK;
    }

    async EnableAutomaticControllerUpdateDownloadAsync(): Promise<Status.OK | ResponseResult<UpdateErrorCode>> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot enable automatic updates download, feature unsupported in current version. Current version: ${ver}`);

        const result = await this._connection.SendAndWaitForResponseAsync<any>(new EnableAutomaticUpdatesRequest(), 10000, false);

        if (result == null || result?.Status != Status.OK)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot enable automatic updates download, response is empty or status is not OK. Status: ${result?.Status}`);

        return Status.OK;
    }

    async DisableAutomaticControllerUpdateDownloadAsync(): Promise<Status.OK | ResponseResult<UpdateErrorCode>> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot disable automatic update download, feature unsupported in current version. Current version: ${ver}`);

        const result = await this._connection.SendAndWaitForResponseAsync<any>(new DisableAutomaticUpdatesRequest(), 10000, false);

        if (result == null || result?.Status != Status.OK)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot disable automatic update download, response is empty or status is not OK. Status: ${result?.Status}`);

        return Status.OK;
    }

    async GetControllerUpdatesHistoryAsync(): Promise<UpdateHistory[] | ResponseResult<UpdateErrorCode>> {
        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot disable automatic update download, feature unsupported in current version. Current version: ${ver}`);

        const result = await this._connection.SendAndWaitForResponseAsync<UpdateApiObject[]>(new GetUpdatesHistoryRequest(), 10000, false);

        if (result == null)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response is empty.`);

        switch (result.Status) {
            case Status.OK:
                if (result.Data != null)
                    return result.Data.map(d => {
                        const update = new UpdateHistory();
                        update.UpdatedResourceName = ParseObjToMap<string, string>(d.UpdateName);
                        update.UpdateDescription = ParseObjToMap<string, string>(d.UpdateDescription);
                        update.UpdateCreationTime = d.Creationtime;
                        update.UpdateChannel = d.UpdateChannel;
                        update.Version = `${d.Version.BaseRuntime}.${d.Version.ContainerSoftwareVersion}`;

                        return update;
                    });
                else
                    return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response data is empty.`);

            case Status.NoData:
                return [];

            default:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response status is not OK. Status: ${result.Status}`);
        }
    }

    InstallControllerUpdateAsync<T extends ICustomUpdateActions>(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, updateGuid: string, downloadOnly: boolean, updateAction?: UpdateAction<T> | undefined): Promise<ResponseResult<UpdateErrorCode> | Status.OK>;
    InstallControllerUpdateAsync<T extends ICustomUpdateActions>(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, updateAction?: UpdateAction<T> | undefined): Promise<ResponseResult<UpdateErrorCode> | Status.OK>;
    async InstallControllerUpdateAsync<T extends ICustomUpdateActions>(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, guidOrAction?: unknown, downloadOnlyOrAction?: unknown, updateAction?: UpdateAction<T> | undefined): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {

        const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");
        const numberOfUpdates = this._controllerUpdates.length;

        const ver = (await this.getRuntimeInfoAsync());

        if (ver instanceof ResponseResult) {
            return ver;
        }

        const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
        const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);

        //Check if any updates are avaliable
        if (this._controllerUpdates.length == 0)
            return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot find any updates, make sure you check for available updates by calling CheckControllerUpdateAsync().`);

        //Install single update
        if ((typeof guidOrAction === 'string') && regGuid.test(guidOrAction as string)) {
            let updateResult: ResponseResult<UpdateErrorCode> | Status.OK;
            const updateToInstall = this._controllerUpdates.find(update => update.UpdateIdentifier == guidOrAction);

            if (updateToInstall == null)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Update with requested guid (${guidOrAction as string}) not found.`);

            this._logger.Debug(`Installing single update type: ${updateToInstall.Update.UpdateType}, resource: ${updateToInstall.UpdatedResourceName}, version: ${updateToInstall.Update.Version}`)

            this._appStateService.DisallowHibernation();

            switch (updateToInstall.UpdateType) {
                case ControllerUpdateType.RadioUpdate:
                    if (downloadOnlyOrAction as boolean)
                        return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded set flag downloadOnly to false to install it.`);

                    updateResult = await (await this._updateProviders.GetUpdatesProviderAsync<IControllerModulesUpdatesProviderService>(updateToInstall.ProviderName)).InstallRadioUpdateAsync(updateToInstall, updateProgress, updateAction);
                    this._appStateService.AllowHibernation();

                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);

                    return updateResult;

                case ControllerUpdateType.SoftwareUpdate:
                    //Old way
                    if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                        if (downloadOnlyOrAction as boolean)
                            return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded set flag downloadOnly to false to install it.`);

                        updateResult = await this.UpdateControllerUsingScriptAsync(updateToInstall.Update, updateProgress);
                        this._appStateService.AllowHibernation();

                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);

                    }
                    //New way
                    else {
                        let availableUpdate = await this.CheckControllerUpdatesAsync();
                        let isDownloaded = availableUpdate.UpdatesAvailable.any(a => a.IsDownloadOnly);
                        updateResult = await this.UpdateControllerUsingApiAsync(updateProgress, downloadProgress, isDownloaded ? false : downloadOnlyOrAction as boolean);
                        this._appStateService.AllowHibernation();

                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);
                    }

                    return updateResult;

                default:
                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update with type ${updateToInstall.UpdateType} is not supported by UpdateService.`)
            }
        }
        //Install all updates
        else {
            //Handle update progress with multiple updates.
            const updateNumberRef = [1];

            const updateProgressWrapper = (progressInfo: UpdateProgressInfo) => {
                progressInfo.UpdateNumber = updateNumberRef[0];
                progressInfo.AvailableUpdates = numberOfUpdates;

                updateProgress(progressInfo);
            }

            const timeout = async (ms: number) => {
                return new Promise(resolve => setTimeout(resolve, ms));
            };

            //First insall radio updates
            let updatesToInstall = this._controllerUpdates.where(update => update.UpdateType == ControllerUpdateType.RadioUpdate).toArray();
            let result = [];

            this._appStateService.DisallowHibernation();
            for (let i = 0; i < updatesToInstall.length; i++) {
                const updateResult = await (await this._updateProviders.GetUpdatesProviderAsync<IControllerModulesUpdatesProviderService>(updatesToInstall[i].ProviderName)).InstallRadioUpdateAsync(updatesToInstall[i], updateProgressWrapper, guidOrAction as UpdateAction<T> | undefined);

                //Wait between updates
                await timeout(5000);

                if (updateResult == Status.OK)
                    this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updatesToInstall[i].UpdateIdentifier);

                result.push(updateResult)
                updateNumberRef[0]++;
            }

            //Install other updates (in the future)
            //....//
            //

            //Install controller update
            const controlerUpdate = this._controllerUpdates.find(update => update.UpdateType == ControllerUpdateType.SoftwareUpdate);

            if (controlerUpdate != null) {
                //Old way
                if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                    const updateResult = await this.UpdateControllerUsingScriptAsync(controlerUpdate.Update, updateProgressWrapper);

                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != controlerUpdate.UpdateIdentifier);

                    result.push(updateResult)
                    updateNumberRef[0]++;
                }
                //New way
                else {
                    const updateResult = await this.UpdateControllerUsingApiAsync(updateProgressWrapper, downloadProgress, downloadOnlyOrAction as boolean);
                    this._appStateService.AllowHibernation();

                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);

                    result.push(updateResult)
                    updateNumberRef[0]++;
                }
            }

            const resultWithError = result.where(r => r instanceof ResponseResult).toArray();
            if (resultWithError != null && resultWithError.length != 0) {
                let errors: string = "";
                resultWithError.forEach(err => errors += `${(err as ResponseResult<UpdateErrorCode>).Type}, `);
                this._appStateService.AllowHibernation();

                if (resultWithError.any(err => (err as ResponseResult<UpdateErrorCode>).Type == UpdateErrorCode.UpdateDownloadFailed))
                    return new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed - not all updates were successfully downloaded. Installation error codes: ${errors}`);

                return new ResponseResult(UpdateErrorCode.UpdatesPartiallyInstalled, `Update installation finished - not all updates were successfully installed. Installation error codes: ${errors}`);
            }

            this._appStateService.AllowHibernation();
            return Status.OK;
        }
    }

    protected async UpdateControllerUsingScriptAsync(update: Update, updateProgress: UpdateProgress): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        switch (update.UpdateType) {
            case UpdateTypes.Container:
            case UpdateTypes.ContainerSoftware:
                try {
                    await this._connection?.SendAndHandleResponseAsync<UpdateStatusFrame>(new ScriptControllerUpdateRequest(update.DownloadUri), this._updateRequestTimeout, (resp) => {
                        const progressInfo = new UpdateProgressInfo();
                        progressInfo.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);

                        switch (resp.Status) {
                            case Status.MultiDataResponseStart:
                                progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                break;

                            case Status.MultiDataResponse:
                                if (resp.Data == null) {
                                    this._logger.Warning(UpdatesService.ServiceName, `Recived update response without data! URI: ${update.DownloadUri}`);
                                    return;
                                }

                                progressInfo.Percentage = Math.round(resp.Data.Progress) as Progress;
                                switch (resp.Data.State) {
                                    case UpdateState.CheckingApplicability:
                                    case UpdateState.CheckingIntegrity:
                                    case UpdateState.CheckingPackageIntegrity:
                                        progressInfo.Status = UpdateStatus.CheckingPackage;
                                        break;
                                    case UpdateState.CreatingBackup:
                                    case UpdateState.MigratingData:
                                    case UpdateState.Removing:
                                        progressInfo.Status = UpdateStatus.MovingData;
                                        break;
                                    case UpdateState.Deploying:
                                    case UpdateState.Installing:
                                        progressInfo.Status = UpdateStatus.Upgrading;
                                        break;
                                    case UpdateState.Downloading:
                                        progressInfo.Status = UpdateStatus.DownloadingPackage;
                                        break;
                                    case UpdateState.Error:
                                        progressInfo.Status = UpdateStatus.Error;
                                        break;
                                    case UpdateState.Installed:
                                        progressInfo.Status = UpdateStatus.Upgraded;
                                        break;
                                    case UpdateState.PreparingSystem:
                                    case UpdateState.ReadyToDownload:
                                        progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                        break;
                                    case UpdateState.UndoingInstallation:
                                        progressInfo.Status = UpdateStatus.InstallationStopped;
                                        break;
                                    case UpdateState.Unpackaging:
                                        progressInfo.Status = UpdateStatus.MovingData;
                                        break;
                                    case UpdateState.WaitingForUser:
                                        progressInfo.Status = UpdateStatus.Upgrading;
                                        break;
                                }
                                break;

                            case Status.MultiDataResponseStop:
                                progressInfo.Status = UpdateStatus.OK;
                                break;

                            case Status.Error:
                            case Status.FatalError:
                                progressInfo.Status = UpdateStatus.Error;
                                break;
                        }
                        updateProgress(progressInfo);
                    }, false);

                    return Status.OK;
                } catch (error) {
                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Cannot process update, error: ${error}`)
                }
            case UpdateTypes.RuntimeFixingScript:
                var howManyDevices = 10;

                let extVersion = "n/a"
                try {
                    extVersion = (await this._extensionService.GetExtensionsInfoAsync()).first(ext => ext.ExtensionGuid == "f4f45dac-beda-441a-8d29-efc6dc977cd1").ExtensionVersion;
                } catch (error) {
                    this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                }

                var updateExtVersion = extVersion;

                const ver = (await this.getRuntimeInfoAsync());
                if (ver instanceof ResponseResult) {
                    return ver;
                }

                var isNewApi = parseFloat(ver.RuntimeVersion) >= 2.0 ? true : (parseFloat(updateExtVersion) >= 1.2201125);

                try {
                    howManyDevices = (await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync()).length;
                }
                catch (error) {
                    this._logger.Warning(UpdatesService.ServiceName, `Cannot get number of devices in system! Error: ${error}. Using default value of 10 devices.`)
                }

                if (isNewApi) {
                    let intervalId: NodeJS.Timeout;
                    let progressInfo = new UpdateProgressInfo();
                    progressInfo.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    progressInfo.Percentage = 1;
                    progressInfo.Status = UpdateStatus.PreparingForUpgrade;

                    updateProgress(progressInfo);

                    let i = 2;
                    const startTime = Date.now();
                    const howLongToUpgrade = (howManyDevices * 900) + 1600000;
                    const onePercentInTime = howLongToUpgrade / 100;
                    const willEndAt = startTime + howLongToUpgrade;

                    const updatePromise = new Promise<ResponseResult<UpdateErrorCode> | Status.OK>(async (resolve, reject) => {
                        try {
                            const OnReturnedFromSuspensionHandler = () => {
                                this._appStateService.OnReturnedFromSuspension().Unsubscribe(OnReturnedFromSuspensionHandler);

                                if (Date.now() > willEndAt) {
                                    clearInterval(intervalId);
                                    this._logger.Debug(`Update already finished!`);

                                    progressInfo.Percentage = 100;
                                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                    progressInfo.Status = UpdateStatus.Upgraded;

                                    updateProgress(progressInfo);
                                    resolve(Status.OK);
                                }
                                else {
                                    var msToEnd = (willEndAt - Date.now());

                                    if (msToEnd < howLongToUpgrade) {
                                        i = (95 - (msToEnd / onePercentInTime));
                                    }
                                    else {
                                        clearInterval(intervalId);
                                        intervalId = setInterval(() => {
                                            clearInterval(intervalId);

                                            progressInfo.Percentage = 100;
                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                            progressInfo.Status = UpdateStatus.Upgraded;

                                            updateProgress(progressInfo);
                                            resolve(Status.OK);
                                        }, msToEnd);
                                    }
                                }
                            }
                            this._appStateService.OnReturnedFromSuspension().Subscribe(OnReturnedFromSuspensionHandler);
                            intervalId = setInterval(() => {
                                if (i > 99) {
                                    clearInterval(intervalId);

                                    progressInfo.Percentage = 100;
                                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                    progressInfo.Status = UpdateStatus.Upgraded;

                                    updateProgress(progressInfo);
                                    resolve(Status.OK);
                                }
                                else {
                                    var percent = i;

                                    if (percent < 99) {
                                        progressInfo.Percentage = Math.round(percent) as Progress;
                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                        progressInfo.Status = UpdateStatus.Upgrading;

                                        updateProgress(progressInfo);
                                    }
                                }
                                i++;
                            }, onePercentInTime);

                            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new InstallRequest(update.DownloadUri), this._updateRequestTimeout, false);

                            if (result.Status != Status.OK) {
                                clearInterval(intervalId);
                                progressInfo.Percentage = 0;
                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                progressInfo.Status = UpdateStatus.InstallationError;

                                updateProgress(progressInfo);
                                this._logger.Debug(UpdatesService.ServiceName, `Update failed with error. Update response status: ${result.Status}`);
                                resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. Update response status: ${result.Status}`));
                            }
                        }
                        catch (error) {
                            this._logger.Debug(UpdatesService.ServiceName, `Update failed with error. ${error}`);

                            progressInfo.Percentage = 0;
                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                            progressInfo.Status = UpdateStatus.Error;
                            updateProgress(progressInfo);

                            clearInterval(intervalId);
                            resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. ${error}`));
                        }
                    })
                    return await updatePromise;
                }
                else {
                    let progressInfo = new UpdateProgressInfo();
                    progressInfo.UpdatedResourceName = new Map<string, string>([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    progressInfo.Percentage = 1;
                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                    progressInfo.Status = UpdateStatus.PreparingForUpgrade;

                    updateProgress(progressInfo);

                    const currentAuthData = this._connection.GetAuthorizationInfo();
                    if (currentAuthData == null)
                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Cannot process update. Cannot get current controller login credentials!`)

                    const result = await this._connection?.SendAndWaitForResponseAsync<any>(new LoginUserRequest({ Email: `support@support`, Password: `support@${currentAuthData.SerialNumber}:${currentAuthData.PIN}` }), 12000, false);
                    if (result.Status === Status.OK) {
                        progressInfo.Percentage = 1;
                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                        progressInfo.Status = UpdateStatus.PreparingForUpgrade;

                        updateProgress(progressInfo);

                        let executeCommandResult = await this._connection?.SendAndWaitForResponseAsync<any>(new ExecuteCommandRequest("rm /runtime/tmp/update.sh"), 12000, false);
                        if (executeCommandResult.Status == Status.OK) {
                            this._logger.Debug(`Removed previous script`);
                            progressInfo.Percentage = 2;
                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                            progressInfo.Status = UpdateStatus.PreparingForUpgrade;

                            updateProgress(progressInfo);

                            executeCommandResult = await this._connection?.SendAndWaitForResponseAsync<any>(new ExecuteCommandRequest(`wget ${update.DownloadUri} -O /runtime/tmp/update.sh --no-check-certificate`), this._updateRequestTimeout, false);
                            if (executeCommandResult.Status == Status.OK) {
                                this._logger.Debug(`Downloaded update script`);
                                progressInfo.Percentage = 5;
                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                progressInfo.Status = UpdateStatus.PreparingForUpgrade;

                                updateProgress(progressInfo);

                                try {
                                    executeCommandResult = await this._connection?.SendAndWaitForResponseAsync<any>(new ExecuteCommandRequest(`chmod a+x /runtime/tmp/update.sh`), 12000, false);
                                    if (executeCommandResult.Status == Status.OK) {
                                        this._logger.Debug(`Executed update script`);
                                        progressInfo.Percentage = 5;
                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                        progressInfo.Status = UpdateStatus.Upgrading;

                                        updateProgress(progressInfo);

                                        let i = 0;
                                        let intervalId: NodeJS.Timeout;
                                        const onePercent = 95 / 100.0;
                                        const startTime = Date.now();
                                        const howLongToUpgrade = (howManyDevices * 900) + 1500000; //ms
                                        const onePercentInTime = howLongToUpgrade / 95;
                                        const willEndAt = startTime + howLongToUpgrade;

                                        const updatePromise = new Promise<ResponseResult<UpdateErrorCode> | Status.OK>(async (resolve, reject) => {
                                            try {
                                                const OnReturnedFromSuspensionHandler = () => {
                                                    this._appStateService.OnReturnedFromSuspension().Unsubscribe(OnReturnedFromSuspensionHandler);

                                                    if (Date.now() > willEndAt) {
                                                        clearInterval(intervalId);
                                                        this._logger.Debug(`Update already finished!`);

                                                        progressInfo.Percentage = 100;
                                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                                        progressInfo.Status = UpdateStatus.Upgraded;

                                                        updateProgress(progressInfo);
                                                        resolve(Status.OK);
                                                    }
                                                    else {
                                                        var msToEnd = (willEndAt - Date.now());

                                                        if (msToEnd < howLongToUpgrade) {
                                                            i = (95 - (msToEnd / onePercentInTime));
                                                        }
                                                        else {
                                                            clearInterval(intervalId);
                                                            intervalId = setInterval(() => {
                                                                clearInterval(intervalId);

                                                                progressInfo.Percentage = 100;
                                                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                                                progressInfo.Status = UpdateStatus.Upgraded;

                                                                updateProgress(progressInfo);
                                                                resolve(Status.OK);
                                                            }, msToEnd);
                                                        }
                                                    }
                                                }
                                                this._appStateService.OnReturnedFromSuspension().Subscribe(OnReturnedFromSuspensionHandler);
                                                intervalId = setInterval(() => {
                                                    if (i > 99) {
                                                        clearInterval(intervalId);

                                                        progressInfo.Percentage = 100;
                                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                                        progressInfo.Status = UpdateStatus.Upgraded;

                                                        updateProgress(progressInfo);
                                                        resolve(Status.OK);
                                                    }
                                                    else {
                                                        var percent = (5 + (i * onePercent));;

                                                        if (percent < 99) {
                                                            progressInfo.Percentage = Math.round(percent) as Progress;
                                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                                            progressInfo.Status = UpdateStatus.Upgrading;

                                                            updateProgress(progressInfo);
                                                        }
                                                    }
                                                    i++;
                                                }, onePercentInTime);

                                                this._connection?.SendAndWaitForResponseAsync<any>(new ExecuteCommandRequest(`/runtime/tmp/update.sh`), 12000, false);
                                            }
                                            catch (error) {
                                                resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. ${error}`));
                                            }
                                        })
                                        return await updatePromise;
                                    } else {
                                        progressInfo.Percentage = 0;
                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage) as Progress;
                                        progressInfo.Status = UpdateStatus.InstallationError;

                                        updateProgress(progressInfo);
                                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed!`)
                                    }
                                } catch (err) {
                                    this._connection?.SendAndWaitForResponseAsync<any>(new ExecuteCommandRequest(`/runtime/scripts/restart_all.sh`), 12000, false);
                                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Error ${err}`)
                                }
                            }
                            return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Cannot clear old update script!`);
                        }
                    }
                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Cannot loggin using support account!`)
                }
            default:
                return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Not supported update type, type: ${update.UpdateType}`)
        }
    }

    private async DownloadControllerUpdateNewApiAsync(pendingUpdate: PendingUpdateApiObject, updateProgress: UpdateProgress, downloadProgess: DownloadProgress, downloadOnly: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        return new Promise<ResponseResult<UpdateErrorCode> | Status.OK>(async (resolve, reject) => {
            let downloadProgressEndPercentage = 50;

            if (!downloadOnly)
                downloadProgressEndPercentage = 100;

            let downloadProgressOnePercentageMultiplier = 100 / downloadProgressEndPercentage;

            await this._connection.SendAndHandleResponseAsync<UpdateProgressResponse>(new DownloadUpdateRequest(), this._updateRequestTimeout, (resp) => {
                const info = new DownloadProgressInfo();
                info.DownloadOnly = downloadOnly;
                info.UpdatedResourceName = ParseObjToMap<string, string>(pendingUpdate.Update.UpdateName);

                switch (resp.Status) {
                    case Status.MultiDataResponseStart:
                        info.Percentage = 0;
                        info.SpeedMbps = 0;
                        info.PercentageTotal = 0;
                        info.CurrentTask = DownloadTask.DownloadingInstaller
                        downloadProgess(info);
                        break;

                    case Status.MultiDataResponse:
                        if (resp.Data != null) {
                            info.Percentage = resp.Data.StepProgressInPercentage;
                            info.SpeedMbps = resp.Data.DownloadSpeedInMbs;

                            if (downloadOnly)
                                info.PercentageTotal = Math.round((resp.Data?.UpdateProgressInPercentage as number) * downloadProgressOnePercentageMultiplier) as Progress;
                            else
                                info.PercentageTotal = Math.round(resp.Data?.UpdateProgressInPercentage) as Progress;

                            switch (resp.Data.UpdateStep) {
                                case UpdateStep.DownloadingInstaller:
                                    info.CurrentTask = DownloadTask.DownloadingInstaller;
                                    break;
                                case UpdateStep.DownloadingUpdate:
                                    info.CurrentTask = DownloadTask.DownloadingUpdate;
                                    break;

                                case UpdateStep.UpdateDownloaded:
                                    info.Percentage = 100;

                                    if (downloadOnly)
                                        info.PercentageTotal = Math.round((resp.Data?.UpdateProgressInPercentage as number) * downloadProgressOnePercentageMultiplier) as Progress;
                                    else
                                        info.PercentageTotal = Math.round(resp.Data?.UpdateProgressInPercentage) as Progress;

                                    info.SpeedMbps = 0;
                                    info.CurrentTask = DownloadTask.CheckingUpdate;
                                    downloadProgess(info);
                                    info.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                    downloadProgess(info);
                                    resolve(Status.OK);
                                    break;

                                default:
                                    info.CurrentTask = DownloadTask.CheckingUpdate;
                                    break;
                            }

                            downloadProgess(info);
                        }
                        break;

                    case Status.MultiDataResponseStop:
                        if (resp.Data != null) {
                            switch (resp.Data.UpdateStep) {
                                case UpdateStep.UpdateDownloaded:
                                    info.Percentage = 100;

                                    if (downloadOnly)
                                        info.PercentageTotal = Math.round((resp.Data?.UpdateProgressInPercentage as number) * downloadProgressOnePercentageMultiplier) as Progress;
                                    else
                                        info.PercentageTotal = Math.round(resp.Data?.UpdateProgressInPercentage) as Progress;

                                    info.SpeedMbps = 0;
                                    info.CurrentTask = DownloadTask.CheckingUpdate;
                                    downloadProgess(info);
                                    info.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                    downloadProgess(info);
                                    resolve(Status.OK);
                                    break;

                                case UpdateStep.UpdateDownloadFailed:
                                    info.Percentage = 0;
                                    info.PercentageTotal = 0;
                                    info.SpeedMbps = 0;
                                    info.CurrentTask = DownloadTask.Unknown
                                    downloadProgess(info);
                                    resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateDownloadFailed, `Update download failed!`));
                            }
                        }
                        break;

                    case Status.Error:
                    case Status.FatalError:
                        info.Percentage = 0;
                        info.PercentageTotal = 0;
                        info.SpeedMbps = 0;
                        info.CurrentTask = DownloadTask.Unknown
                        downloadProgess(info);
                        resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotProcessUpdate, `Update download failed!`));
                        break;
                }
            }, false);
        });
    }

    private async InstallUpdateNewApiAsync(pendingUpdate: PendingUpdateApiObject, updateProgress: UpdateProgress, downloadProgress: DownloadProgress, downloadOnly: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        return new Promise<ResponseResult<UpdateErrorCode> | Status.OK>(async (resolve, reject) => {
            const endPercentage = 100;
            const totalTime = this._updateOfflineStageTime * 60 * 1000; // 9.3 minutes
            let percentage = 0;
            const info = new UpdateProgressInfo();
            let intervalId: any = null;
            pendingUpdate.DownloadOnly = false;

            const onReconnect = async (state: AppState) => {
                if (state == AppState.Connected) {
                    clearInterval(intervalId);
                    info.Status = UpdateStatus.Upgraded;
                    info.Percentage = 100;
                    info.PercentageTotal = 100;
                    updateProgress(info);
                    clearInterval(intervalId);
                    await Task.Delay(5000);
                    resolve(Status.OK);
                    Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Unsubscribe(onReconnect);
                }
            };
            await this._connection.SendAndHandleResponseAsync<UpdateProgressResponse>(new InstallUpdateRequest(), this._updateRequestTimeout, (resp) => {
                info.UpdatedResourceName = ParseObjToMap<string, string>(pendingUpdate.Update.UpdateName);

                switch (resp.Status) {
                    case Status.MultiDataResponseStart:
                        info.Status = UpdateStatus.PreparingForUpgrade;
                        info.Percentage = resp.Data?.StepProgressInPercentage as Progress;
                        info.PercentageTotal = resp.Data?.UpdateProgressInPercentage as Progress;

                        updateProgress(info);
                        break;

                    case Status.MultiDataResponse:
                        if (resp.Data != null) {
                            info.Status = UpdateStatus.Upgrading;
                            info.Percentage = Math.round(resp.Data.StepProgressInPercentage) as Progress;
                            info.PercentageTotal = Math.round(resp.Data.UpdateProgressInPercentage) as Progress;

                            percentage = resp.Data.StepProgressInPercentage;
                            updateProgress(info);
                        }
                        break;

                    case Status.MultiDataResponseStop:
                        if (resp.Data != null) {
                            switch (resp.Data.UpdateStep) {
                                case UpdateStep.UpdateInstallationFailed:
                                    info.Status = UpdateStatus.InstallationError;
                                    info.Percentage = 0;
                                    info.PercentageTotal = 0;
                                    updateProgress(info);
                                    resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                                    break;

                                //Add 5 minutes to finish offline stage
                                case UpdateStep.OfflineUpdateStep:
                                    {
                                        info.Percentage = Math.round(resp.Data.StepProgressInPercentage) as Progress;
                                        info.PercentageTotal = Math.round(resp.Data.UpdateProgressInPercentage) as Progress;
                                        let oneUpdateProgressPercent = (100 - info.PercentageTotal) / 100;
                                        let updateProgressPercent = info.PercentageTotal;
                                        //info.PercentageTotal = resp.Data.UpdateProgressInPercentage as Progress; // 78%

                                        const incrementTime = totalTime / (endPercentage - percentage);
                                        intervalId = setInterval(() => {
                                            percentage++;
                                            updateProgressPercent += oneUpdateProgressPercent;

                                            if (updateProgressPercent > 100)
                                                updateProgressPercent = 100;

                                            if (percentage >= endPercentage) {
                                                info.Status = UpdateStatus.Upgraded;
                                                info.Percentage = 100;
                                                info.PercentageTotal = 100;
                                                updateProgress(info);
                                                clearInterval(intervalId);
                                                resolve(Status.OK);
                                            }
                                            else {
                                                info.Status = UpdateStatus.Upgrading;
                                                info.Percentage = Math.round(percentage) as Progress;
                                                info.PercentageTotal = Math.round(updateProgressPercent) as Progress;
                                                updateProgress(info);
                                            }

                                        }, incrementTime);

                                        Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Subscribe(onReconnect);
                                    }
                                    break;
                            }
                        }
                        break;

                    case Status.Error:
                    case Status.FatalError:
                        info.Status = UpdateStatus.Error;
                        info.Percentage = 0;
                        info.PercentageTotal = 0;
                        updateProgress(info);
                        resolve(new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                        break;
                }
            }, false);
        });
    }

    protected async InstallControllerUpdateUsingApiAsync(updateProgress: UpdateProgress, downloadProgress: DownloadProgress): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        const result = await this._connection.SendAndWaitForResponseAsync<PendingUpdateApiObject>(new PendingUpdatesRequest(), 10000, false);

        if (result?.Status == Status.NoData)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.DeviceIsUpToDate, `No updates available!`);

        if (result == null || result?.Status != Status.OK || result?.Data == null)
            throw new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result?.Status}`)

        switch (result.Data.UpdateStep) {
            case UpdateStep.DownloadingInstaller:
            case UpdateStep.DownloadingUpdate:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded, or download in progress.`);
            case UpdateStep.InstallingUpdate:
            case UpdateStep.PreparingUpdate:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateAlreadyInstalled, `Update already installed, or installation in progress.`);
            case UpdateStep.UpdateDownloaded:
                result.Data.DownloadOnly = false; // We are installing update, not downloading it
                return await this.InstallUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);

            default:
                this._logger.Debug(UpdatesService.ServiceName, `Installing update using new API. Update step: ${result.Data.UpdateStep} downloadOnly: false`);
                result.Data.DownloadOnly = false; // We are installing update and downloading it
                var downloadResult = await this.DownloadControllerUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);

                if (downloadResult != Status.OK)
                    return downloadResult;
                else
                    return await this.InstallUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);
        }
    }

    protected async DownloadControllerUpdateUsingApiAsync(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, downloadOnly: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        const result = await this._connection.SendAndWaitForResponseAsync<PendingUpdateApiObject>(new PendingUpdatesRequest(), 10000, false);
        if (result?.Status == Status.NoData)
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.DeviceIsUpToDate, `No updates available!`);

        if (result == null || result?.Status != Status.OK || result?.Data == null)
            throw new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result?.Status}`)

        switch (result.Data.UpdateStep) {
            case UpdateStep.DownloadingInstaller:
            case UpdateStep.DownloadingUpdate:
            case UpdateStep.UpdateDownloaded:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded, or download is in progress.`);
            case UpdateStep.InstallingUpdate:
            case UpdateStep.PreparingUpdate:
                return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.UpdateAlreadyInstalled, `Update already installed, or installation is in progress.`);
            default:
                return await this.DownloadControllerUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, downloadOnly);
        }
    }

    protected async UpdateControllerUsingApiAsync(updateProgress: UpdateProgress, downloadProgress: DownloadProgress, downloadOnly: boolean): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        if (downloadOnly)
            return await this.DownloadControllerUpdateUsingApiAsync(updateProgress, downloadProgress, true);
        else
            return await this.InstallControllerUpdateUsingApiAsync(updateProgress, downloadProgress);
    }

    private async getRuntimeInfoAsync(): Promise<SoftwareRuntimeInfo | ResponseResult<UpdateErrorCode>> {
        try {
            return await this._updateProviders.GetSoftwareRuntimeInfoAsync();
        } catch (error) {
            if (error instanceof CannotGetRuntimeInfo) {
                if (error.code == CannotGetRuntimeErrorCode.Timeout)
                    return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetCurrentControllerVersionTimeout, `Cannot get runtime info (current controller version), timeout error - please restart controller to fix issue.`);
            }
            return new ResponseResult<UpdateErrorCode>(UpdateErrorCode.CannotGetCurrentControllerVersion, `Cannot get runtime info (current controller version), error: ${error}`);
        }
    }
}

class CachedUpdateInfo extends UpdateInfo {
    UpdateIdentifier: string = "";
    ProviderName: string = "";
    UpdateType: ControllerUpdateType | null = null;
}

/**
 * @type Class
 */
export class ControllerUpdateNotAvailableInfo {
    UpdatedResourceName: Map<string, string> = new Map<string, string>();
    CurrentResourceVersion: string = "";
    UpdateType: ControllerUpdateType | null = null;
    ResponseResult: ResponseResult<UpdateErrorCode> | null = null;
}

class ControllerUpdate implements IControllerAvailableUpdateInfo {
    private _updateIdentifier: string = "";
    private _updateResourceName: Map<string, string> = new Map<string, string>();
    private _updateType: ControllerUpdateType | null = null;
    private _currentResourceVersion: string = "";
    private _newResourceVersion: string = "";
    private _updateDescription: Map<string, string> = new Map<string, string>();
    private _updateIsDownloading: boolean = false;
    private _updateCheckDownloadProgress: number = 0;
    private _updateCreationTime: string | null = null;
    private _isDownloadOnly: boolean = false;


    get IsDownloadOnly(): boolean {
        return this._isDownloadOnly;
    }

    set IsDownloadOnly(value: boolean) {
        this._isDownloadOnly = value;
    }

    get UpdateCreationTime(): string | null {
        return this._updateCreationTime;
    }
    get DownloadProgress(): Progress {
        return this._updateCheckDownloadProgress as Progress;
    }
    get UpdateDescription(): Map<string, string> {
        return this._updateDescription;
    }
    get UpdateIdentifier(): string {
        return this._updateIdentifier;
    }
    get UpdatedResourceName(): Map<string, string> {
        return this._updateResourceName;
    }
    get UpdateType(): ControllerUpdateType | null {
        return this._updateType;
    }
    get CurrentResourceVersion(): string {
        return this._currentResourceVersion;
    }
    get NewResourceVersion(): string {
        return this._newResourceVersion;
    }
    get UpdateIsDownloading(): boolean {
        return this._updateIsDownloading;
    }
    set UpdateCreationTime(value: string | null) {
        this._updateCreationTime = value;
    }
    set DownloadProgress(value: number) {
        this._updateCheckDownloadProgress = value;
    }
    set UpdateDescription(value: Map<string, string>) {
        this._updateDescription = value;
    }
    set UpdateIdentifier(value: string) {
        this._updateIdentifier = value;
    }
    set UpdatedResourceName(value: Map<string, string>) {
        this._updateResourceName = value;
    }
    set UpdateType(value: ControllerUpdateType | null) {
        this._updateType = value;
    }
    set CurrentResourceVersion(value: string) {
        this._currentResourceVersion = value;
    }
    set NewResourceVersion(value: string) {
        this._newResourceVersion = value;
    }
    set UpdateIsDownloading(value: boolean) {
        this._updateIsDownloading = value;
    }
}

class DeviceUpdate implements IDeviceUpdateInfo {
    private _updatedResourceName: string = "";
    private _updateResourceGuid: string = "";
    private _protocol: string = "";
    private _protocolGuid: string = "";
    private _currentResourceVersion: string = "";
    private _newResourceVersion: string = "";
    private _isUpdateSafe: boolean = true;
    private _updateDescription: Map<string, string> = new Map<string, string>();

    get UpdateDescription(): Map<string, string> {
        return this._updateDescription;
    }

    set UpdateDescription(value: Map<string, string>) {
        this._updateDescription = value;
    }

    get UpdatedResourceName(): string {
        return this._updatedResourceName;
    }
    get UpdateResourceGuid(): string {
        return this._updateResourceGuid;
    }
    get Protocol(): string {
        return this._protocol;
    }
    get ProtocolGuid(): string {
        return this._protocolGuid;
    }
    get CurrentResourceVersion(): string {
        return this._currentResourceVersion;
    }
    get NewResourceVersion(): string {
        return this._newResourceVersion;
    }
    get IsUpdateSafe(): boolean {
        return this._isUpdateSafe;
    }
    set UpdatedResourceName(value: string) {
        this._updatedResourceName = value;
    }
    set UpdateResourceGuid(value: string) {
        this._updateResourceGuid = value;
    }
    set Protocol(value: string) {
        this._protocol = value;
    }
    set ProtocolGuid(value: string) {
        this._protocolGuid = value;
    }
    set CurrentResourceVersion(value: string) {
        this._currentResourceVersion = value;
    }
    set NewResourceVersion(value: string) {
        this._newResourceVersion = value;
    }
    set IsUpdateSafe(value: boolean) {
        this._isUpdateSafe = value;
    }
}

//Install update
class ScriptControllerUpdateRequest extends DataFrame<string> {
    constructor(uri: string) {
        super();
        this.Resource = "/updates/update/from/uri";
        this.Method = Method.Post;
        this.Data = uri;
    }
}

class UpdateStatusFrame {
    ExpectedInstallationTime: number = 0;
    Guid: string = "";
    Package: JsonPackageSchema = new JsonPackageSchema();
    Progress: Progress = 0;
    State: UpdateState = UpdateState.PreparingSystem;
    UserInput: UserInput = UserInput.Install;
}

class JsonPackageSchema {
    Package: JsonPackage = new JsonPackage();
    Publisher: Publisher = new Publisher();
}

class JsonPackage {
    CompatibleHardwareVersions: Hardware[] = [];
    CompatibleOperatingSystems: OperatingSystem[] = [];
    CompatibleProcessorArch: ProcessorArch[] = [];
    CompatibleSoftwareVersions: number[] = [];
    DependencyRequiredExtensions: DependencyExtension[] = [];
    Description: Map<number, string> = new Map<number, string>();
    Guid: string = "";
    Name: Map<number, string> = new Map<number, string>();
    PackageAPIVersion: number = 0;
    PackageType: PackageType = PackageType.Unknown;
    PackageVersion: number = 0;
    PublicationTime: string = Date.now().toString();
    PublisherGuid: string = "";
    RequiredDownloadDiskSpace: number = 0;
    RequiredInstallDiskSpace: number = 0;
    UpdateChannels: string[] = [];
}

class Hardware {
    Guid: string = "";
    MaxVersion: number = 0;
    MinVersion: number = 0;

}

enum OperatingSystem {
    Windows = 0,
    Linux = 1,
    Unix = 2,
    OSX = 3,
    Android = 4,
    iOS = 5,
    WindowsMobile = 6
}

enum ProcessorArch {
    Any = 0,
    MIPS = 1,
    ARM32 = 2,
    ARM64 = 3,
    IntelX86 = 4,
    IntelX64 = 5
}

class DependencyExtension {
    CompatibleVersions: number = 0;
    ExtensionGuid: number = 0;
    MaximumCompatibleVersion: number = 0;
    MinimumCompatibleVersion: number = 0;
}

enum PackageType {
    Unknown = 0,
    Environment = 1,
    OperatingSystem = 2,
    Application = 3,
    Extension = 4,
    HardwareFirmware = 5,
    Script = 6,
    Command = 7
}

class Publisher {
    Description: string = "";
    Email: string = "";
    Guid: string = "";
    Name: string = "";
    Phone: string = "";
    PublisherGuid: string = "";
    Webpage: string = "";
}

class PendingUpdateApiObject {
    public Id: string = "";
    public Update: UpdateApiObject = new UpdateApiObject();
    public UpdateDownloadPath: string = "";
    public InstallerDownloadPath: string = "";
    public DownloadDateTime: string = "";
    public UpdateStep: UpdateStep = UpdateStep.Unknown;
    public StepProgressInPercentage: number = 0;
    public UpdateProgressInPercentage: number = 0;
    public DownloadSpeed: number = 0;
    public DownloadProgress: number = 0;
    public DownloadOnly: boolean = false;
    public RawUpdateJsonFile: string = "";
}
class UpdateApiObject {
    public Guid: string = "";
    public Creationtime: string = "";
    public Version: UpdateVersion = new UpdateVersion();
    public UpdateChannel: string = "";
    public UpdateFileHash: string = "";
    public UpdateFileUrl: string = "";
    public VerifySign: string = "";
    public InstallerFileUrl: string = "";
    public InstallerFileHash: string = "";
    public InstallerVerifySign: string = "";
    public UpdateName: any
    public UpdateDescription: any;
}

class DeviceUpdateInfoApiObject {
    public DeviceGuid: string = "";
    public IsAvailable: boolean = false;
    public NewVersion: string = "";
    public CurrentVersion: string = "";
}

class UpdateVersion {
    public BaseRuntime: number = 0;
    public ContainerSoftwareVersion: number = 0;
}

enum UpdateStep {
    InstallerFileSignCheckFailed = -5,
    UpdateFileSignCheckFailed = -4,
    UpdateInstallationFailed = -3,
    UpdateDownloadFailed = -2,
    UpdateFileCheckFailed = -1,
    Unknown = 0,
    DownloadingUpdate = 1,
    CheckingUpdate = 2,
    PreparingUpdate = 3,
    InstallingUpdate = 4,
    DownloadingInstaller = 5,
    UpdateDownloaded = 6,
    OfflineUpdateStep = 7
}

class UpdateProgressResponse {
    UpdateStep: UpdateStep = UpdateStep.Unknown;
    StepProgressInPercentage: Progress = 0;
    UpdateProgressInPercentage: Progress = 0;
    DownloadSpeedInMbs: number = 0;
}

class InstallRequest extends DataFrame<string> {
    constructor(uri: string) {
        super();
        this.Resource = "/updates/install/from/url";
        this.Method = Method.Put;
        this.Data = uri;
    }
}

class LoginUserRequest extends DataFrame<LoginUser> {
    constructor(data: LoginUser) {
        super();
        this.Resource = "/users/user/login";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class LoginUser {
    Email: string = "";
    Password: string = "";
}

class ExecuteCommandRequest extends DataFrame<string> {
    constructor(command: string) {
        super();
        this.Resource = "/controller/runtime/execute";
        this.Method = Method.Post;
        this.Data = command;
    }
}

class GetUpdateBranchRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/branch";
        this.Method = Method.Get;
    }
}

class SetUpdateBranchRequest extends DataFrame<UpdateBranch> {
    constructor(data: UpdateBranch) {
        super();
        this.Resource = "/updates/branch";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class CancelFailedUpdateRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/failed/cancel";
        this.Method = Method.Put;
    }
}

class PendingUpdateProgressRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/pending/progress";
        this.Method = Method.Get;
    }
}

class PendingUpdatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/pending";
        this.Method = Method.Get;
    }
}

class CheckUpdatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/check";
        this.Method = Method.Get;
    }
}

class DownloadUpdateRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/download";
        this.Method = Method.Post;
    }
}

class InstallUpdateRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/install";
        this.Method = Method.Post;
    }
}

class GetAutomaticUpdatesStatusRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/status";
        this.Method = Method.Get;
    }
}
class EnableAutomaticUpdatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/enable";
        this.Method = Method.Put;
    }
}

class DisableAutomaticUpdatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/disable";
        this.Method = Method.Put;
    }
}

class GetUpdatesHistoryRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/history";
        this.Method = Method.Get;
    }
}

class CheckDevicesUpdatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/updates/devices/availability";
        this.Method = Method.Get;
    }
}
